import { DecimalPipe } from '@angular/common';
import {
  RESOURCE_ANCHOR_COLOR,
  RESOURCE_AVAILABLE_COLOR,
  RESOURCE_COMPONENT_COLOR,
  RESOURCE_DEDUCTION_COLOR,
  RESOURCE_GAIN_COLOR,
} from '../../output-components/chart-palette';
import { GB_PER_TB } from '../../../core/services/quick-sizer-response.mapper';
import { QuickSizerApiCapacitySegment, QuickSizerCapacityWaterfall, StorageEfficiency } from '../models/quick-sizer.models';
import { VCF_COMPONENTS_CHARGED_LABEL } from './component-charge-note';

/** `−` deducts, `+` adds back, `=` restates a running total. The gutter makes it a waterfall. */
export type WaterfallOperator = '' | '−' | '+' | '=';

export interface WaterfallRow {
  operator: WaterfallOperator;
  label: string;
  /** The engine's label when the row is shown under a different name. Absent when they match. */
  sourceLabel?: string;
  /** Display value in TB. Empty on `group` rows. */
  display: string;
  percent?: number;
  /** `group` is a section heading: label only, no figure and no bar. */
  kind: 'baseline' | 'group' | 'overhead' | 'subtotal' | 'result';
  color: string;
  /** Bar left edge as a share of the axis. */
  offsetPct: number;
  widthPct: number;
  tooltip?: string;
}

export interface WaterfallModel {
  rows: WaterfallRow[];
  /** The sentence above the chart, on screen and in the PDF. */
  caption: string;
  /** An overhead term other than Data reduction came back below zero. Shown, not clamped. */
  hasNegativeOverhead: boolean;
}

/**
 * `capacity.overheadBreakdown` is the whole waterfall in order: raw capacity, vSAN terms, usable
 * (physical, only with data reduction), the data reduction gain, usable (logical), VCF component
 * storage and available for workloads. Totals are restatements, not deductions.
 */
const RAW_CAPACITY_LABEL = /^raw capacity$/i;
const PHYSICAL_USABLE_LABEL = /usable capacity \(physical\)/i;
const LOGICAL_USABLE_LABEL = /usable capacity \(logical\)/i;
const VCF_COMPONENT_STORAGE_LABEL = /vcf component storage/i;
const AVAILABLE_FOR_WORKLOADS_LABEL = /available for workloads/i;
/** Dedup/compression savings, sent negative so raw minus every term equals logical usable. */
const DATA_REDUCTION_LABEL = /^data reduction$/i;

function isTotalLabel(label: string): boolean {
  return RAW_CAPACITY_LABEL.test(label)
    || PHYSICAL_USABLE_LABEL.test(label)
    || LOGICAL_USABLE_LABEL.test(label)
    || VCF_COMPONENT_STORAGE_LABEL.test(label)
    || AVAILABLE_FOR_WORKLOADS_LABEL.test(label);
}

/**
 * The `Available for workloads` row, the storage pool the max-VM table divides. Falls back to
 * logical usable.
 */
export function availableForWorkloadsGb(source: QuickSizerCapacityWaterfall): number {
  if (!source) {
    return 0;
  }
  const row = source.overheads.find(item => AVAILABLE_FOR_WORKLOADS_LABEL.test(item.label));
  return row?.valueGb ?? source.logicalUsableGb;
}

export const VSAN_GROUP_LABEL = 'vSAN overheads and reserves';
export const DATA_REDUCTION_GROUP_LABEL = 'Data reduction';
export const VCF_GROUP_LABEL = 'VCF platform';

export const RAW_CAPACITY_DISPLAY_LABEL = 'Total Raw Storage';
export const VSAN_USABLE_LABEL = 'Usable after vSAN overheads';
export const SPACE_SAVED_LABEL = 'Global Dedup Cost Savings';
export const REDUCED_USABLE_LABEL = 'Usable after data reduction';
export const AVAILABLE_LABEL = 'Available for workloads';

export const VSAN_USABLE_TOOLTIP =
  'Disk space left once vSAN has taken what it needs to run and protect your data: file system, '
  + 'caching tier, RAID copies, metadata and reserved free space.';

/** The forward sizer's Global Dedup Cost Savings help. */
export const SPACE_SAVED_TOOLTIP = 'Cost savings achieved through global deduplication in vSAN ESA (VCF 9.1.0+)';

/** Compression Only has no dedup, and the forward sizer shows no dedup savings for it. */
export const COMPRESSION_SAVED_LABEL = 'Compression Savings';

export const COMPRESSION_SAVED_TOOLTIP =
  'Space saved by vSAN ESA compression, at the data reduction ratio you entered.';

export const REDUCED_USABLE_TOOLTIP =
  'How much data you can store once the data reduction savings are added back. It can be more '
  + 'than the raw disk space.';

export const AVAILABLE_TOOLTIP =
  'What is left for workload VMs after vSAN overheads, any data reduction savings, and the '
  + 'storage for enabled VCF platform components.';

/**
 * Help for each deduction, keyed by the engine's label in lower case. Terms the forward sizer also
 * shows use its text, from app labels, its disk space chart help and its Summary card.
 */
export const DEDUCTION_TOOLTIPS: Readonly<Record<string, string>> = {
  'host rebuild reserve':
    'The Host Rebuild Reserve (HRR) setting accounts for the capacity needed to absorb a sustained '
    + 'failure of a single host in a vSAN cluster, to support an N+1 cluster design strategy',
  'file system overhead':
    'File system overhead is the space consumed by the on-disk file system on capacity drives, separate '
    + 'from overhead due to deduplication, compression, or checksums. Based on per disk capacity, this '
    + 'could be anywhere between 2% to 6% of total capacity.',
  'performance tier overhead':
    'Performance tier overhead refers to the resources consumed by the caching and performance-related '
    + 'functions of the vSAN architecture, impacting overall system performance and capacity utilization. '
    + 'Performance tier overhead is 5% of total raw storage.',
  'free space reserve':
    'Space kept empty so vSAN has room to rebalance data and finish maintenance tasks.',
  'operations reserve': 'Operations Reserve is the space reserved for vSAN\'s internal operations',
  'raid overhead':
    'Extra copies or parity data vSAN writes so your data survives a disk or host failure. '
    + 'The RAID policy sets how much.',
  'object overhead':
    'Object overhead in vSAN represents the additional capacity consumed beyond the raw data for '
    + 'maintaining data availability, optimizing performance, and ensuring the smooth operation of the '
    + 'distributed storage environment. Object overhead is about 13.1% of the Effective Used Capacity.',
  'deduplication overhead': 'Metadata overhead specific to deduplication in vSAN ESA (VCF 9.1.0+)',
  'vcf component storage': 'Disk space for the VCF management components you enabled, such as vCenter and NSX.',
};

/** Forward sizer names for the engine's terms, keyed by the engine's label in lower case. */
const DISPLAY_LABELS: Readonly<Record<string, string>> = {
  'host rebuild reserve': 'Host Rebuild Reserve',
  'file system overhead': 'File System Overhead',
  'performance tier overhead': 'Perf Tier Overhead',
  'operations reserve': 'Operations Reserve',
  'object overhead': 'Object Overhead',
  'deduplication overhead': 'Dedup Metadata Overhead',
};

function displayLabel(engineLabel: string): string {
  return DISPLAY_LABELS[engineLabel.toLowerCase()] ?? engineLabel;
}

export const NEGATIVE_OVERHEAD_WARNING =
  'A storage overhead came back as a negative number, which should not happen. It is drawn as '
  + 'returned, so the totals above may not add up. Please report this sizing to the Quick Sizer team.';

function captionFor(hasDataReduction: boolean, hasVcfStorage: boolean): string {
  const steps = ['Start from raw disk space', 'take away what vSAN uses and reserves'];
  if (hasDataReduction) {
    steps.push('add back the space data reduction saves');
  }
  if (hasVcfStorage) {
    steps.push('take away storage for VCF platform components');
  }
  const last = steps.pop();
  return `${steps.join(', ')}, then ${last}.`;
}

/**
 * The capacity waterfall: one staircase from raw capacity to what workloads get, in up to three
 * sections (vSAN overheads and reserves, data reduction, VCF platform), each closed by a `=` total.
 * Computes no capacity figure: every number shown is a response field. Shared by the component and
 * the PDF.
 *
 * @param componentTooltip how the VCF components figure is worked out; the generic help when absent
 * @param storageEfficiency the mode the result was sized with; names the data reduction row
 */
export function buildWaterfallRows(
  source: QuickSizerCapacityWaterfall,
  decimal: DecimalPipe,
  componentTooltip?: string,
  storageEfficiency?: StorageEfficiency,
): WaterfallModel {
  if (!source) {
    return { rows: [], caption: '', hasNegativeOverhead: false };
  }

  const tb = (valueGb: number) => `${decimal.transform(valueGb / GB_PER_TB, '1.2-2') ?? '0'} TB`;

  const { rawCapacityGb, logicalUsableGb } = source;
  const all = source.overheads;
  const find = (label: RegExp) => all.find(item => label.test(item.label));

  const physicalRow = find(PHYSICAL_USABLE_LABEL);
  const logicalRow = find(LOGICAL_USABLE_LABEL);
  const availableRow = find(AVAILABLE_FOR_WORKLOADS_LABEL);
  const dataReduction = all.find(item => DATA_REDUCTION_LABEL.test(item.label) && item.valueGb !== 0);
  const vcfRow = find(VCF_COMPONENT_STORAGE_LABEL);
  const availableGb = availableForWorkloadsGb(source);
  const hasVcfStorage = (vcfRow?.valueGb ?? 0) > 0;

  // Zero terms are dropped (e.g. dedup when off); negatives are kept.
  const vsanTerms = all.filter(item =>
    !isTotalLabel(item.label) && !DATA_REDUCTION_LABEL.test(item.label) && item.valueGb !== 0);
  const hasNegativeOverhead = all.some(item =>
    !isTotalLabel(item.label)
    && !DATA_REDUCTION_LABEL.test(item.label)
    && item.valueGb < 0);

  // Data reduction can lift the running total above raw, so the axis is the widest point reached.
  let peak = rawCapacityGb;
  let walk = rawCapacityGb;
  for (const item of dataReduction ? [...vsanTerms, dataReduction] : vsanTerms) {
    walk -= item.valueGb;
    peak = Math.max(peak, walk);
  }
  const axis = Math.max(peak, rawCapacityGb, logicalUsableGb, availableGb, physicalRow?.valueGb ?? 0) || 1;
  const span = (value: number) => (value / axis) * 100;

  const rows: WaterfallRow[] = [{
    operator: '',
    label: RAW_CAPACITY_DISPLAY_LABEL,
    display: tb(rawCapacityGb),
    percent: 100,
    kind: 'baseline',
    color: RESOURCE_ANCHOR_COLOR,
    offsetPct: 0,
    widthPct: span(rawCapacityGb),
  }];

  const heading = (label: string) => rows.push({
    operator: '',
    label,
    display: '',
    kind: 'group',
    color: '',
    offsetPct: 0,
    widthPct: 0,
  });

  // Each step hangs between the running total before and after it.
  let running = rawCapacityGb;
  const step = (item: QuickSizerApiCapacitySegment, color: string, label = item.label, tooltip?: string) => {
    const after = running - item.valueGb;
    const gain = item.valueGb < 0;
    rows.push({
      operator: gain ? '+' : '−',
      label,
      ...(label === item.label ? {} : { sourceLabel: item.label }),
      display: tb(Math.abs(item.valueGb)),
      // The operator carries the sign, so figure and share are unsigned.
      percent: item.percentOfRaw === undefined ? undefined : Math.abs(item.percentOfRaw),
      kind: 'overhead',
      color,
      offsetPct: span(gain ? running : after),
      widthPct: span(Math.abs(item.valueGb)),
      ...(tooltip ? { tooltip } : {}),
    });
    running = after;
  };

  const total = (
    kind: 'subtotal' | 'result',
    label: string,
    valueGb: number,
    tooltip: string,
    sourceRow?: QuickSizerApiCapacitySegment,
  ) => rows.push({
    operator: '=',
    label,
    ...(sourceRow && sourceRow.label !== label ? { sourceLabel: sourceRow.label } : {}),
    display: tb(valueGb),
    percent: sourceRow?.percentOfRaw,
    kind,
    color: kind === 'result' ? RESOURCE_AVAILABLE_COLOR : RESOURCE_ANCHOR_COLOR,
    offsetPct: 0,
    widthPct: span(valueGb),
    tooltip,
  });

  if (vsanTerms.length) {
    heading(VSAN_GROUP_LABEL);
    vsanTerms.forEach(item => step(item, RESOURCE_DEDUCTION_COLOR, displayLabel(item.label),
      DEDUCTION_TOOLTIPS[item.label.toLowerCase()]));
  }

  if (dataReduction) {
    if (physicalRow) {
      total('subtotal', VSAN_USABLE_LABEL, physicalRow.valueGb, VSAN_USABLE_TOOLTIP, physicalRow);
    }
    heading(DATA_REDUCTION_GROUP_LABEL);
    if (storageEfficiency === 'COMPRESSION_ONLY') {
      step(dataReduction, RESOURCE_GAIN_COLOR, COMPRESSION_SAVED_LABEL, COMPRESSION_SAVED_TOOLTIP);
    } else {
      step(dataReduction, RESOURCE_GAIN_COLOR, SPACE_SAVED_LABEL, SPACE_SAVED_TOOLTIP);
    }
  }

  if (vcfRow && hasVcfStorage) {
    total(
      'subtotal',
      dataReduction ? REDUCED_USABLE_LABEL : VSAN_USABLE_LABEL,
      logicalUsableGb,
      dataReduction ? REDUCED_USABLE_TOOLTIP : VSAN_USABLE_TOOLTIP,
      logicalRow,
    );
    heading(VCF_GROUP_LABEL);
    step(vcfRow, RESOURCE_COMPONENT_COLOR, VCF_COMPONENTS_CHARGED_LABEL,
      componentTooltip ?? DEDUCTION_TOOLTIPS[vcfRow.label.toLowerCase()]);
  }

  total('result', AVAILABLE_LABEL, availableGb, AVAILABLE_TOOLTIP, availableRow ?? logicalRow);

  return { rows, caption: captionFor(!!dataReduction, hasVcfStorage), hasNegativeOverhead };
}
